import BootstrapModal from 'react-bootstrap/Modal';

import 'css/src/components/common/ui/modal';

import React from 'react';
import classNames from 'classnames';

import { LoadingBackdrop } from 'js/src/components/common/ui/Backdrop';
import {
    BUTTON_COLORS,
    BUTTON_PADDING,
    BUTTON_SIZES,
    BUTTON_VARIANTS,
    IconButton,
} from 'js/src/components/common/ui/Button';
import { prefersReducedMotion } from 'js/src/libs/css-helpers';
import { isTesting } from 'js/src/libs/custom-utils';

export enum MODAL_SIZES {
    DEFAULT = 'default',
    SM = 'sm',
    LG = 'lg',
    XL = 'xl',
    FS = 'fs',
}

type Props = {
    showing: boolean;
    className?: string;
    onHidden?: VoidFunction;
    onShown?: VoidFunction;
    title?: string | React.ReactNode;
    size?: MODAL_SIZES;
    unsetOverflow?: boolean;
    closeable?: boolean;
    backdrop?: boolean | 'static';
    backdropClassName?: string;
    dialogClassName?: string;
    bodyClassName?: string;
    beforeShown?: VoidFunction;
    animation?: string;
    loadingContents?: boolean;
    children?: React.ReactNode;
    footer?: React.ReactNode;
    footerClassName?: string;
    showFooterDivider?: boolean;
    footerRef?: React.Ref<HTMLDivElement>;
    noPadding?: boolean;
    noFooterPadding?: boolean;
    centered?: boolean;
    stableGutters?: boolean;
    onMoveBack?: VoidFunction;
};

const Modal = React.memo(function Modal({
    showing,
    className,
    onHidden,
    onShown,
    title,
    size = MODAL_SIZES.DEFAULT,
    unsetOverflow,
    closeable = true,
    backdrop,
    backdropClassName,
    dialogClassName,
    bodyClassName,
    beforeShown,
    animation,
    loadingContents = false,
    children,
    footer,
    footerClassName,
    showFooterDivider,
    footerRef,
    noPadding,
    noFooterPadding = false,
    centered = true,
    stableGutters,
    onMoveBack,
}: Props) {
    /*
    Use this modal class to easily add and control modals from React.

    Example usage:
        <Modal
            showing={this.state.showing}
            title="Modal Title"
            className="my-modal"
            onHidden={this._closeModal}
        >
            <h2>This is in the modal body</h2>
            <p>Add components, inputs, buttons, etc here!</p>
        </Modal>

    Any children passed to Modal will be the modal's contents.

    The showing property controls
    if the modal is showing, so likely should be stored in the caller's state.  onHidden is called
    when the modal is requested to be closed by the modal-specific functions (clicking the 'x'
    button, pressing 'escape', clicking on the backdrop) which should be handled and used to hide
    the modal.

    Modals are attached to the DOM directly and are not children of the parent React component,
    so styling must be scoped separately.  Pass in a className to use to scope each modal's
    SCSS.
    */

    const usesAnimation = React.useMemo(() => {
        return animation !== 'off' && !prefersReducedMotion() && !isTesting();
    }, [animation]);

    const onHide = React.useCallback(() => {
        // hide modal
        if (typeof onHidden === 'function') {
            if (showing) {
                // double-clicking on the modal backdrop can cause onHide to be called twice,
                // we check showing to avoid calling onHidden twice
                onHidden();
            }
        }
    }, [onHidden, showing]);

    const onEntered = React.useCallback(() => {
        // show modal
        if (typeof onShown === 'function') {
            onShown();
        }
    }, [onShown]);

    const onEnter = React.useCallback(() => {
        if (typeof beforeShown === 'function') {
            beforeShown();
        }
    }, [beforeShown]);

    const onShow = React.useCallback(() => {
        /*
        React Bootstrap uses a transition from react-transition-groups when showing the modal:
        https://reactcommunity.org/react-transition-group/transition-group

        This controls the animation (fade and slight downward movement) when the modal is shown, and
        the opposite animation when the modal is closed.

        However, this is only used when the BootstrapModal has the `animation` prop set to true.

        We turn off this prop for modals when the user turns on PREFERS_REDUCED_MOTION, which is an
        OS-level accessibility setting.

        When the TransitionGroup is used, the order of callbacks when showing the animation are:

        - onEnter: animation is beginning
        - onShow: modal elements have been added to the DOM (not triggered by TransitionGroup)
        - onEntered: animation is ending

        However, if the TransitionGroup is not used (`BootstrapModal.animation` prop is false),
        none of the TransitionGroup callbacks are called. However, the onShow callback is called,
        so here we just use this callback to call all the required callbacks, to offer a consistent
        API for our Modal, whether the user has PREFERS_REDUCED_MOTION on or off.
        */
        if (!usesAnimation) {
            onEnter();
            onEntered();
        }
    }, [onEnter, onEntered, usesAnimation]);

    const moveBackIcon = React.useMemo(() => {
        if (onMoveBack) {
            return (
                <IconButton
                    onClick={onMoveBack}
                    icon="arrow_back_ios"
                    className="modal-moveback-button"
                    size={BUTTON_SIZES.SM}
                    color={BUTTON_COLORS.TERTIARY}
                    variant={BUTTON_VARIANTS.OUTLINE}
                    padding={BUTTON_PADDING.NONE}
                />
            );
        }
        return null;
    }, [onMoveBack]);

    const modalTitle = React.useMemo(() => {
        if (title) {
            const titleElement = (
                <BootstrapModal.Title
                    className="font-l5 font-l5--h5 w-100"
                    data-sharewithclosebtn={closeable}
                >
                    {moveBackIcon}
                    {title}
                </BootstrapModal.Title>
            );
            if (typeof title === 'string') {
                return <span title={title}>{titleElement}</span>;
            }
            return titleElement;
        }
        return null;
    }, [closeable, moveBackIcon, title]);

    const bootstrapSize =
        size === MODAL_SIZES.DEFAULT || size === MODAL_SIZES.FS ? undefined : size;

    // setup modal
    return (
        <BootstrapModal
            animation={usesAnimation}
            show={showing}
            centered={centered}
            className={classNames(className, `modal-dialog-wrapper-${size}`)}
            backdrop={backdrop}
            onHide={onHide}
            onEnter={onEnter}
            onEntered={onEntered}
            onShow={onShow}
            size={bootstrapSize}
            dialogClassName={classNames(dialogClassName, {
                'overflow-unset': unsetOverflow,
                'modal-default': size === MODAL_SIZES.DEFAULT,
                'modal-fs': size === MODAL_SIZES.FS,
            })}
            backdropClassName={classNames(backdropClassName, `modal-backdrop-${size}`)}
        >
            {(closeable || modalTitle) && (
                <>
                    <BootstrapModal.Header
                        className="px-l5-24 py-l5-16"
                        closeButton={closeable !== false}
                    >
                        {modalTitle}
                    </BootstrapModal.Header>
                    {title && <hr />}
                </>
            )}
            <BootstrapModal.Body
                className={classNames(bodyClassName, {
                    'px-l5-24 py-l5-20': !noPadding,
                    'p-l5-0': noPadding,
                    'scrollbar-gutter-stable': stableGutters,
                })}
            >
                {children}
            </BootstrapModal.Body>
            {footer && (
                <BootstrapModal.Footer
                    ref={footerRef}
                    className={classNames(
                        {
                            'px-l5-24 py-l5-16': !noFooterPadding,
                            'p-l5-0': noFooterPadding,
                            'border-top-0': !showFooterDivider,
                        },
                        footerClassName,
                    )}
                >
                    {footer}
                </BootstrapModal.Footer>
            )}
            <LoadingBackdrop className="modal-contents-loading" show={loadingContents} />
        </BootstrapModal>
    );
});

export default Modal;
